import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  ConfirmDialog,
  ExtendedCard,
  FormAlert,
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  FormSection,
  SettingsButton,
} from "@NP-Webber/crm-kit";
import {
  AccountBalanceWalletOutlinedIcon,
  AccountTreeOutlinedIcon,
  ArchiveOutlinedIcon,
  DescriptionOutlinedIcon,
  EditOutlinedIcon,
  MergeTypeOutlinedIcon,
  PeopleOutlinedIcon,
} from "../../ui/appIcons";
import { QuietButton } from "../../ui/QuietButton";
import {
  BILLING_DAY_OPTIONS,
  CONTACT_ROLE_KEY_OPTIONS,
  LookupSelect,
} from "../../ui/LookupSelect";
import { useLookupOptions } from "../../ui/useLookupOptions";
import {
  useEntityLabels,
  type EntityLabels,
} from "../../i18n/EntityLabelsProvider";
import { useAuth } from "../../auth/AuthProvider";
import {
  customersApi,
  type ActivityItem,
  type Customer,
  type CustomerAlias,
  type CustomerContact,
  type Project,
} from "../../api/customersApi";
import { shellApi } from "../../api/shellApi";
import { toClientApiError } from "../../api/httpClient";
import { UiStateMessage } from "../../engines";
import type { UiLoadState } from "../../engines";
import { CustomerFinancePanel } from "./CustomerFinancePanel";
import { CustomerLedgerPanel } from "./CustomerLedgerPanel";
import { CustomerSubscriptionsPanel } from "./CustomerSubscriptionsPanel";
import { CustomerPaymentsPanel } from "./CustomerPaymentsPanel";
import { CustomerDebtsPanel } from "./CustomerDebtsPanel";
import { CustomerMailHistoryPanel } from "./CustomerMailHistoryPanel";
import { IncomeExpensesPanel } from "../cashLedger/IncomeExpensesPanel";
import {
  CUSTOMER_CARD_PANEL_DEFS,
  resolveCustomerCardPanels,
  type CustomerCardPanelKey,
} from "./customerCardPanels";

const PANEL_PERMISSION_FALLBACK = CUSTOMER_CARD_PANEL_DEFS.map(
  (def) => def.permissionKey,
);

function normalizeCustomerActivity(item: unknown, labels: EntityLabels) {
  const activity = item as ActivityItem;
  if (!activity?.id) {
    return null;
  }
  const activityLabels: Record<string, string> = {
    "customer.created": `נוצר ${labels.customer}`,
    "customer.updated": `עודכן ${labels.customer}`,
    "customer.archived": "בוטל",
    "customer.mandate_expiring": "תוקף הרשאה עומד לפוג",
    "customer.contact_linked": "קושר איש קשר",
    "customer.project_linked": `קושר ${labels.project}`,
    "customer.alias_added": "נוסף כינוי",
    "customer.alias_removed": "הוסר כינוי",
    "customer.merged": `מוזג ${labels.customer}`,
  };
  return {
    id: `act-${activity.id}`,
    type: "activity" as const,
    subtype: activity.eventType,
    title: activityLabels[activity.eventType] ?? activity.eventType,
    description: "",
    status: "",
    createdAt: activity.createdAt,
    updatedAt: activity.createdAt,
    createdBy: null,
    rawData: activity,
  };
}

function isCustomerClosed(status: string): boolean {
  return status === "cancelled" || status === "archived";
}

function canCancelCustomerStatus(status: string): boolean {
  return status === "active" || status === "frozen";
}

export type CustomerCardProps = {
  open: boolean;
  customerId: string | null;
  onClose: () => void;
  onChanged?: () => void;
};

export function CustomerCard({
  open,
  customerId,
  onClose,
  onChanged,
}: CustomerCardProps) {
  const labels = useEntityLabels();
  const auth = useAuth();
  const [activeId, setActiveId] = useState(customerId ?? "");
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [aliases, setAliases] = useState<CustomerAlias[]>([]);
  const [customerOptions, setCustomerOptions] = useState<Customer[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [grantedPermissions, setGrantedPermissions] = useState<string[]>([]);
  const [state, setState] = useState<UiLoadState>("loading");
  const [error, setError] = useState<string | undefined>();
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactRole, setContactRole] = useState("");
  const [contactRoleKey, setContactRoleKey] = useState<
    "primary" | "secondary" | "reference"
  >("primary");
  const [contactBusy, setContactBusy] = useState(false);
  const [contactAddOpen, setContactAddOpen] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [projectKind, setProjectKind] = useState<"unit" | "program">("unit");
  const [projectBusy, setProjectBusy] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveCancelReasonKey, setArchiveCancelReasonKey] = useState("");
  const [aliasName, setAliasName] = useState("");
  const [aliasBusy, setAliasBusy] = useState(false);
  const [aliasToDelete, setAliasToDelete] = useState<CustomerAlias | null>(null);
  const [deleteAliasBusy, setDeleteAliasBusy] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [mergeConfirmOpen, setMergeConfirmOpen] = useState(false);
  const [mergeBusy, setMergeBusy] = useState(false);

  const { optionsByList } = useLookupOptions([
    "cities",
    "billing_methods",
    "lead_sources",
    "comm_prefs",
    "charge_statuses",
    "cancel_reasons",
  ]);
  const cityOptions = optionsByList.cities ?? [];
  const billingMethodOptions = optionsByList.billing_methods ?? [];
  const leadSourceOptions = optionsByList.lead_sources ?? [];
  const commPrefOptions = optionsByList.comm_prefs ?? [];
  const chargeStatusOptions = optionsByList.charge_statuses ?? [];
  const cancelReasonOptions = optionsByList.cancel_reasons ?? [];

  useEffect(() => {
    if (open && customerId) {
      setActiveId(customerId);
    }
    if (!open) {
      setEditOpen(false);
      setMergeOpen(false);
      setMergeConfirmOpen(false);
      setArchiveOpen(false);
      setArchiveCancelReasonKey("");
      setAliasToDelete(null);
      setSaveError(null);
      setContactAddOpen(false);
    }
  }, [open, customerId]);

  const load = useCallback(async () => {
    if (!open || !activeId) {
      return;
    }
    setState("loading");
    setError(undefined);
    try {
      const [
        item,
        contactRows,
        projectRows,
        activityRows,
        aliasRows,
        allCustomers,
        permissionKeys,
      ] = await Promise.all([
        customersApi.get(activeId),
        customersApi.listContacts(activeId),
        customersApi.listProjects(activeId),
        customersApi.listActivity(activeId),
        customersApi.listAliases(activeId),
        customersApi.list(),
        shellApi.listGrantedPermissions().catch(() => PANEL_PERMISSION_FALLBACK),
      ]);
      setCustomer(item);
      setContacts(contactRows);
      setProjects(projectRows);
      setActivity(activityRows);
      setAliases(aliasRows);
      setGrantedPermissions(permissionKeys);
      setCustomerOptions(
        allCustomers.filter((entry) => entry.id !== activeId && entry.status === "active"),
      );
      setFormValues({
        name: item.name,
        phone: item.phone ?? "",
        email: item.email ?? "",
        address: item.address ?? "",
        regulatoryId: item.regulatoryId ?? "",
        cityKey: item.cityKey ?? "",
        communityName: item.communityName ?? "",
        generalCommunity: item.generalCommunity ?? "",
        joinDate: item.joinDate ?? "",
        sourceKey: item.sourceKey ?? "",
        commPrefKey: item.commPrefKey ?? "",
        notes: item.notes ?? "",
        logoUrl: item.logoUrl ?? "",
        lastOutreachOn: item.lastOutreachOn ?? "",
        billingMethodKey: item.billingMethodKey ?? "",
        mandateExpiryOn: item.mandateExpiryOn ?? "",
        payLast4: item.payLast4 ?? "",
        billingDay: item.billingDay != null ? String(item.billingDay) : "",
        monthlyAmount: item.monthlyAmount != null ? String(item.monthlyAmount) : "",
        lastChargeStatusKey: item.lastChargeStatusKey ?? "",
        lastChargeOn: item.lastChargeOn ?? "",
        discourseName: String(item.attributes?.discourseName ?? ""),
        easycountName: String(item.attributes?.easycountName ?? ""),
      });
      setState("ready");
    } catch (err) {
      const apiError = toClientApiError(err);
      if (apiError.status === 403) {
        setState("permission");
      } else if (apiError.status === 404) {
        setError(`${labels.customer} לא נמצא`);
        setState("error");
      } else {
        setError(apiError.message);
        setState("error");
      }
    }
  }, [open, activeId, labels.customer]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    if (!customer) {
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await customersApi.update(customer.id, {
        name: formValues.name,
        phone: formValues.phone || null,
        email: formValues.email || null,
        address: formValues.address || null,
        regulatoryId: formValues.regulatoryId || null,
        cityKey: formValues.cityKey || null,
        communityName: formValues.communityName || null,
        generalCommunity: formValues.generalCommunity || null,
        joinDate: formValues.joinDate || null,
        sourceKey: formValues.sourceKey || null,
        commPrefKey: formValues.commPrefKey || null,
        notes: formValues.notes || null,
        logoUrl: formValues.logoUrl || null,
        lastOutreachOn: formValues.lastOutreachOn || null,
        attributes: {
          discourseName: formValues.discourseName || null,
          easycountName: formValues.easycountName || null,
        },
        billing: {
          billingMethodKey: formValues.billingMethodKey || null,
          mandateExpiryOn: formValues.mandateExpiryOn || null,
          payLast4: formValues.payLast4 || null,
          billingDay: formValues.billingDay ? Number(formValues.billingDay) : null,
          lastChargeStatusKey: formValues.lastChargeStatusKey || null,
          lastChargeOn: formValues.lastChargeOn || null,
          monthlyAmountAgorot: formValues.monthlyAmount
            ? Number(formValues.monthlyAmount)
            : null,
        },
        products: customer.products as Record<string, "active" | "cancelled" | "">,
      });
      setCustomer(updated);
      setEditOpen(false);
      const activityRows = await customersApi.listActivity(customer.id);
      setActivity(activityRows);
      onChanged?.();
    } catch (err) {
      setSaveError(toClientApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddContact = async () => {
    if (!customer || !contactName.trim()) {
      return;
    }
    setContactBusy(true);
    setSaveError(null);
    try {
      await customersApi.createContact({
        displayName: contactName.trim(),
        phone: contactPhone.trim() || undefined,
        email: contactEmail.trim() || undefined,
        roleLabel: contactRole.trim() || undefined,
        roleKey: contactRoleKey,
        customerId: customer.id,
      });
      setContactName("");
      setContactPhone("");
      setContactEmail("");
      setContactRole("");
      setContactRoleKey("primary");
      setContactAddOpen(false);
      const [contactRows, activityRows] = await Promise.all([
        customersApi.listContacts(customer.id),
        customersApi.listActivity(customer.id),
      ]);
      setContacts(contactRows);
      setActivity(activityRows);
    } catch (err) {
      setSaveError(toClientApiError(err).message);
    } finally {
      setContactBusy(false);
    }
  };

  const handleAddProject = async () => {
    if (!customer || !projectName.trim()) {
      return;
    }
    setProjectBusy(true);
    setSaveError(null);
    try {
      await customersApi.createProject({
        name: projectName.trim(),
        kind: projectKind,
        customerId: customer.id,
      });
      setProjectName("");
      setProjectKind("unit");
      const [projectRows, activityRows] = await Promise.all([
        customersApi.listProjects(customer.id),
        customersApi.listActivity(customer.id),
      ]);
      setProjects(projectRows);
      setActivity(activityRows);
    } catch (err) {
      setSaveError(toClientApiError(err).message);
    } finally {
      setProjectBusy(false);
    }
  };

  const handleArchive = async () => {
    if (!customer || !archiveCancelReasonKey) {
      return;
    }
    setArchiveBusy(true);
    try {
      const updated = await customersApi.archive(customer.id, {
        cancelReasonKey: archiveCancelReasonKey,
      });
      setCustomer(updated);
      setArchiveOpen(false);
      setArchiveCancelReasonKey("");
      const activityRows = await customersApi.listActivity(customer.id);
      setActivity(activityRows);
      onChanged?.();
    } catch (err) {
      setSaveError(toClientApiError(err).message);
      setArchiveOpen(false);
      setArchiveCancelReasonKey("");
    } finally {
      setArchiveBusy(false);
    }
  };

  const handleAddAlias = async () => {
    if (!customer || !aliasName.trim()) {
      return;
    }
    setAliasBusy(true);
    setSaveError(null);
    try {
      await customersApi.addAlias(customer.id, aliasName.trim());
      setAliasName("");
      const [aliasRows, activityRows] = await Promise.all([
        customersApi.listAliases(customer.id),
        customersApi.listActivity(customer.id),
      ]);
      setAliases(aliasRows);
      setActivity(activityRows);
    } catch (err) {
      setSaveError(toClientApiError(err).message);
    } finally {
      setAliasBusy(false);
    }
  };

  const handleDeleteAlias = async () => {
    if (!customer || !aliasToDelete) {
      return;
    }
    setDeleteAliasBusy(true);
    setSaveError(null);
    try {
      await customersApi.deleteAlias(customer.id, aliasToDelete.id);
      setAliasToDelete(null);
      const [aliasRows, activityRows] = await Promise.all([
        customersApi.listAliases(customer.id),
        customersApi.listActivity(customer.id),
      ]);
      setAliases(aliasRows);
      setActivity(activityRows);
    } catch (err) {
      setSaveError(toClientApiError(err).message);
      setAliasToDelete(null);
    } finally {
      setDeleteAliasBusy(false);
    }
  };

  const handleMerge = async () => {
    if (!customer || !mergeTargetId) {
      return;
    }
    setMergeBusy(true);
    setSaveError(null);
    try {
      await customersApi.merge(customer.id, mergeTargetId);
      setMergeConfirmOpen(false);
      setMergeOpen(false);
      setActiveId(mergeTargetId);
      onChanged?.();
    } catch (err) {
      setSaveError(toClientApiError(err).message);
      setMergeConfirmOpen(false);
    } finally {
      setMergeBusy(false);
    }
  };

  const isForumKupot = auth.activeTenant?.packKey === "forum-kupot";

  const detailsFields = useMemo(() => {
    if (!customer) {
      return [];
    }
    if (isForumKupot) {
      return [
        { key: "name", label: "שם רשמי", value: customer.name ?? "" },
        {
          key: "communityName",
          label: "שם מתוקנן",
          value: customer.communityName?.trim() || "—",
        },
        {
          key: "regulatoryId",
          label: "ח.פ / ע.ר",
          value: customer.regulatoryId ?? "—",
        },
        { key: "address", label: "כתובת", value: customer.address ?? "—" },
        {
          key: "status",
          label: "סטטוס",
          value:
            customer.status === "active"
              ? "פעיל"
              : customer.status === "frozen"
                ? "מוקפא"
                : "מבוטל",
        },
        { key: "phone", label: "טלפון", value: customer.phone ?? "—" },
        { key: "email", label: "אימייל", value: customer.email ?? "—" },
      ];
    }
    return [
      { key: "name", label: `שם ${labels.customer}`, value: customer.name ?? "" },
      { key: "phone", label: "טלפון", value: customer.phone ?? "—" },
      { key: "email", label: "אימייל", value: customer.email ?? "—" },
      { key: "address", label: "כתובת", value: customer.address ?? "—" },
      {
        key: "regulatoryId",
        label: "מזהה רגולטורי",
        value: customer.regulatoryId ?? "—",
      },
    ];
  }, [customer, labels.customer, isForumKupot]);

  const resolvedPanels = useMemo(
    () =>
      resolveCustomerCardPanels(
        auth.activeTenant?.cardPanels?.customers,
        grantedPermissions,
        auth.activeTenant?.enabledFeatures,
      ),
    [
      auth.activeTenant?.cardPanels?.customers,
      auth.activeTenant?.enabledFeatures,
      grantedPermissions,
    ],
  );

  const showContactsInDetails =
    isForumKupot &&
    !resolvedPanels.some((panel) => panel.key === "contacts") &&
    grantedPermissions.includes("contacts.view");

  const enabledFeatures = auth.activeTenant?.enabledFeatures ?? [];

  if (!open) {
    return null;
  }

  if (state !== "ready" || !customer) {
    return (
      <ExtendedCard
        open
        onClose={onClose}
        title={state === "loading" ? "טוען…" : labels.customer}
        subtitle=""
        detailsFields={[]}
        feedSources={{ activities: [], tasks: [], notes: [] }}
        showDashboard={false}
        detailsHeaderExtra={
          <div className="related-panel">
            <UiStateMessage state={state} errorMessage={error} />
          </div>
        }
      />
    );
  }

  const mergeTargetName =
    customerOptions.find((entry) => entry.id === mergeTargetId)?.name ?? "";

  const panelIcons: Partial<Record<CustomerCardPanelKey, ReactNode>> = {
    contacts: <PeopleOutlinedIcon />,
    subscriptions: <DescriptionOutlinedIcon />,
    payments: <AccountBalanceWalletOutlinedIcon />,
    debts: <AccountBalanceWalletOutlinedIcon />,
    mail_history: <DescriptionOutlinedIcon />,
    projects: <AccountTreeOutlinedIcon />,
    income_expenses: <AccountBalanceWalletOutlinedIcon />,
    finance: <AccountBalanceWalletOutlinedIcon />,
    ledger: <DescriptionOutlinedIcon />,
  };

  const contactsList = (
    <div className="related-panel">
      {contacts.length === 0 ? (
        <p className="related-panel__empty">אין אנשי קשר עדיין.</p>
      ) : (
        <ul className="related-panel__list">
          {contacts.map((contact) => {
            const roleKeyLabel = contact.roleKey
              ? CONTACT_ROLE_KEY_OPTIONS.find((o) => o.value === contact.roleKey)
                  ?.label
              : null;
            return (
              <li
                key={contact.id}
                className="related-panel__item"
                style={{
                  padding: 12,
                  borderRadius: 8,
                  background: "var(--crm-surface-muted, #fafaf8)",
                  border: "1px solid var(--ck-border)",
                  marginBottom: 8,
                }}
              >
                <strong style={{ display: "block", marginBottom: 4 }}>
                  {contact.displayName}
                </strong>
                {roleKeyLabel || contact.roleLabel ? (
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--ck-text-3)",
                      marginBottom: 4,
                    }}
                  >
                    {[roleKeyLabel, contact.roleLabel].filter(Boolean).join(" · ")}
                  </div>
                ) : null}
                {contact.phone ? (
                  <div style={{ fontSize: 13 }}>{contact.phone}</div>
                ) : null}
                {contact.email ? (
                  <div
                    style={{
                      fontSize: 13,
                      color: "var(--crm-info, #2563eb)",
                    }}
                  >
                    {contact.email}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {!isCustomerClosed(customer.status) ? (
        <SettingsButton
          type="button"
          variant="primarySettings"
          size="sm"
          onClick={() => {
            setSaveError(null);
            setContactAddOpen(true);
          }}
        >
          הוספת איש קשר
        </SettingsButton>
      ) : null}
    </div>
  );

  const contactsTab = contactsList;

  const projectsTab = (
    <div className="related-panel">
      {projects.length === 0 ? (
        <p className="related-panel__empty">אין {labels.projects} מקושרים.</p>
      ) : (
        <ul className="related-panel__list">
          {projects.map((project) => (
            <li key={project.id} className="related-panel__item">
              <strong>{project.name}</strong>
              <span>
                {project.kind === "program" ? "תוכנית" : labels.project} ·{" "}
                {project.status === "active" ? "פעיל" : project.status}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="related-panel__form related-panel__form--stack">
        <FormField
          label={`שם ${labels.project}`}
          value={projectName}
          onChange={setProjectName}
          disabled={projectBusy || isCustomerClosed(customer.status)}
        />
        <FormField
          label="סוג"
          type="select"
          value={projectKind}
          onChange={(value) => setProjectKind(value === "program" ? "program" : "unit")}
          disabled={projectBusy || isCustomerClosed(customer.status)}
          options={[
            { value: "unit", label: labels.project },
            { value: "program", label: "תוכנית" },
          ]}
        />
        <SettingsButton
          type="button"
          variant="primarySettings"
          size="sm"
          onClick={() => {
            void handleAddProject();
          }}
          disabled={projectBusy || !projectName.trim() || isCustomerClosed(customer.status)}
        >
          {projectBusy ? "יוצר…" : `יצירת ${labels.project}`}
        </SettingsButton>
      </div>
    </div>
  );

  const aliasesTab = (
    <div className="related-panel">
      {aliases.length === 0 ? (
        <p className="related-panel__empty">אין כינויים עדיין.</p>
      ) : (
        <ul className="related-panel__list">
          {aliases.map((alias) => (
            <li key={alias.id} className="related-panel__item related-panel__item--row">
              <strong>{alias.alias}</strong>
              {customer.status === "active" ? (
                <QuietButton
                  title="מחיקת כינוי"
                  tone="danger"
                  onClick={() => setAliasToDelete(alias)}
                >
                  מחיקה
                </QuietButton>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {customer.status === "active" ? (
        <div className="related-panel__form">
          <FormField
            label="כינוי חדש"
            value={aliasName}
            onChange={setAliasName}
            disabled={aliasBusy}
          />
          <SettingsButton
            type="button"
            variant="primarySettings"
            size="sm"
            onClick={() => {
              void handleAddAlias();
            }}
            disabled={aliasBusy || !aliasName.trim()}
          >
            {aliasBusy ? "מוסיף…" : "הוספה"}
          </SettingsButton>
        </div>
      ) : null}
    </div>
  );

  const panelContent: Record<CustomerCardPanelKey, ReactNode> = {
    contacts: contactsTab,
    subscriptions: (
      <CustomerSubscriptionsPanel
        customerId={customer.id}
        customerName={customer.name}
        archived={isCustomerClosed(customer.status)}
      />
    ),
    payments: <CustomerPaymentsPanel customerId={customer.id} />,
    debts: <CustomerDebtsPanel customerId={customer.id} />,
    mail_history: <CustomerMailHistoryPanel customerId={customer.id} />,
    projects: projectsTab,
    income_expenses: (
      <IncomeExpensesPanel
        customerId={customer.id}
        customerIds={[customer.id]}
        customerNames={{ [customer.id]: customer.name }}
        writeCustomerId={customer.id}
        archived={isCustomerClosed(customer.status)}
        enabledFeatures={enabledFeatures}
      />
    ),
    finance: (
      <CustomerFinancePanel
        customerId={customer.id}
        archived={isCustomerClosed(customer.status)}
      />
    ),
    ledger: <CustomerLedgerPanel customerId={customer.id} />,
    aliases: aliasesTab,
  };

  const extraTabs = resolvedPanels.map((panel) => ({
    label: panel.key === "projects" ? labels.projects : panel.labelHe,
    icon: panelIcons[panel.key],
    content: panelContent[panel.key],
  }));

  return (
    <>
      <ExtendedCard
        key={activeId}
        open
        onClose={onClose}
        title={customer.name}
        subtitle={
          customer.status === "active"
            ? `${labels.customer} פעיל`
            : customer.status === "frozen"
              ? `${labels.customer} מוקפא`
              : "מבוטל"
        }
        detailsFields={detailsFields}
        detailsHeaderExtra={
          <>
            {saveError && !editOpen && !mergeOpen ? (
              <FormAlert severity="error">{saveError}</FormAlert>
            ) : null}
            {showContactsInDetails ? (
              <div className="related-panel" style={{ marginTop: 8 }}>
                <h4
                  style={{
                    margin: "0 0 8px",
                    fontSize: 13,
                    color: "var(--ck-text-3)",
                    fontWeight: 600,
                  }}
                >
                  אנשי קשר
                </h4>
                {contactsList}
              </div>
            ) : null}
          </>
        }
        headerActions={(() => {
          const canCancel =
            canCancelCustomerStatus(customer.status) &&
            grantedPermissions.includes("customers.archive");
          const actions: Array<{
            icon: ReactNode;
            tooltip: string;
            onClick: () => void;
            disabled?: boolean;
          }> = [];
          if (customer.status === "active") {
            actions.push(
              {
                icon: <EditOutlinedIcon />,
                tooltip: "עריכה",
                onClick: () => setEditOpen(true),
              },
              {
                icon: <MergeTypeOutlinedIcon />,
                tooltip: "מיזוג ל…",
                onClick: () => {
                  setMergeTargetId(customerOptions[0]?.id ?? "");
                  setMergeOpen(true);
                },
                disabled: customerOptions.length === 0,
              },
            );
          }
          if (canCancel) {
            actions.push({
              icon: <ArchiveOutlinedIcon />,
              tooltip: `ביטול ${labels.customer}`,
              onClick: () => setArchiveOpen(true),
            });
          }
          return actions;
        })()}
        feedSources={{ activities: activity, tasks: [], notes: [] }}
        feedAdapters={{
          normalizeActivity: (item: unknown) => normalizeCustomerActivity(item, labels),
        }}
        feedTabLabel="יומן פעילות"
        showDashboard={false}
        extraTabs={extraTabs}
      />

      <FormDialog
        open={contactAddOpen}
        onClose={() => {
          if (!contactBusy) {
            setContactAddOpen(false);
            setSaveError(null);
          }
        }}
        size="sm"
      >
        <FormCard
          title="הוספת איש קשר"
          error={saveError}
          onSave={() => {
            void handleAddContact();
          }}
          onCancel={() => {
            setContactAddOpen(false);
            setSaveError(null);
          }}
          saveLabel="הוספה"
          cancelLabel="ביטול"
          saving={contactBusy}
          saveDisabled={contactBusy || !contactName.trim()}
        >
          <FormGrid cols={1}>
            <FormField
              label="שם איש קשר"
              value={contactName}
              onChange={setContactName}
              disabled={contactBusy}
              required
            />
            <FormField
              label="טלפון"
              value={contactPhone}
              onChange={setContactPhone}
              disabled={contactBusy}
            />
            <FormField
              label="אימייל"
              value={contactEmail}
              onChange={setContactEmail}
              disabled={contactBusy}
            />
            <FormField
              label="תפקיד קישור"
              type="select"
              value={contactRoleKey}
              onChange={(value) =>
                setContactRoleKey(value as "primary" | "secondary" | "reference")
              }
              disabled={contactBusy}
              options={CONTACT_ROLE_KEY_OPTIONS}
            />
            <FormField
              label="כותרת תפקיד"
              value={contactRole}
              onChange={setContactRole}
              disabled={contactBusy}
              placeholder="למשל מזכיר"
            />
          </FormGrid>
        </FormCard>
      </FormDialog>

      <FormDialog
        open={editOpen}
        onClose={() => {
          if (!saving) {
            setEditOpen(false);
            setSaveError(null);
          }
        }}
        size="md"
      >
        <FormCard
          title={`עריכת פרטי ${labels.customer}`}
          error={saveError}
          onSave={() => {
            void handleSave();
          }}
          onCancel={() => {
            setEditOpen(false);
            setSaveError(null);
          }}
          saveLabel="שמירה"
          cancelLabel="ביטול"
          saving={saving}
          saveDisabled={!formValues.name?.trim()}
        >
          <FormSection title="זיהוי">
            <FormGrid cols={2}>
              <FormField
                label={isForumKupot ? "שם רשמי" : `שם ${labels.customer}`}
                required
                value={formValues.name ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, name: value }))
                }
                disabled={saving}
              />
              <FormField
                label="קוד קופה"
                value={customer?.code != null ? String(customer.code) : ""}
                disabled
              />
              <FormField
                label={isForumKupot ? "שם מתוקנן" : "שם הקהילה"}
                value={formValues.communityName ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, communityName: value }))
                }
                disabled={saving}
              />
              <FormField
                label="קהילה כללית"
                value={formValues.generalCommunity ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, generalCommunity: value }))
                }
                disabled={saving}
              />
              <LookupSelect
                label="עיר"
                value={formValues.cityKey ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, cityKey: value }))
                }
                options={cityOptions}
                disabled={saving}
                placeholder="חיפוש עיר"
                noOptionsText="אין ערים"
              />
              <FormField
                label="תאריך הצטרפות"
                type="date"
                value={formValues.joinDate ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, joinDate: value }))
                }
                disabled={saving}
              />
              <LookupSelect
                label="מקור הגעה"
                variant="fixed"
                value={formValues.sourceKey ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, sourceKey: value }))
                }
                options={leadSourceOptions}
                disabled={saving}
              />
              <LookupSelect
                label="העדפת תקשורת"
                variant="fixed"
                value={formValues.commPrefKey ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, commPrefKey: value }))
                }
                options={commPrefOptions}
                disabled={saving}
              />
              <FormField
                label="טלפון"
                value={formValues.phone ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, phone: value }))
                }
                disabled={saving}
              />
              <FormField
                label="אימייל"
                value={formValues.email ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, email: value }))
                }
                disabled={saving}
              />
              <FormField
                label="מזהה רגולטורי"
                value={formValues.regulatoryId ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, regulatoryId: value }))
                }
                disabled={saving}
              />
              <FormField
                label="כתובת"
                type="textarea"
                span={2}
                value={formValues.address ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, address: value }))
                }
                disabled={saving}
              />
            </FormGrid>
          </FormSection>
          <FormSection title="גבייה ותחזוקה">
            <FormGrid cols={2}>
              <LookupSelect
                label="אמצעי גבייה"
                variant="fixed"
                value={formValues.billingMethodKey ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, billingMethodKey: value }))
                }
                options={billingMethodOptions}
                disabled={saving}
              />
              <FormField
                label="תוקף הרשאה"
                type="date"
                value={formValues.mandateExpiryOn ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, mandateExpiryOn: value }))
                }
                disabled={saving}
              />
              <FormField
                label="4 ספרות אחרונות"
                value={formValues.payLast4 ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, payLast4: value }))
                }
                disabled={saving}
              />
              <FormField
                label="יום גבייה"
                type="select"
                value={formValues.billingDay ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, billingDay: value }))
                }
                disabled={saving}
                options={[{ value: "", label: "—" }, ...BILLING_DAY_OPTIONS]}
              />
              <LookupSelect
                label="סטטוס גבייה אחרונה"
                variant="fixed"
                value={formValues.lastChargeStatusKey ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({
                    ...prev,
                    lastChargeStatusKey: value,
                  }))
                }
                options={chargeStatusOptions}
                disabled={saving}
              />
              <FormField
                label="מזוהה בפורום"
                value={formValues.discourseName ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, discourseName: value }))
                }
                disabled={saving}
              />
              <FormField
                label="שם באיזיקאונט"
                value={formValues.easycountName ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, easycountName: value }))
                }
                disabled={saving}
              />
              <FormField
                label="הערות"
                type="textarea"
                span={2}
                value={formValues.notes ?? ""}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, notes: value }))
                }
                disabled={saving}
              />
            </FormGrid>
          </FormSection>
          {customer ? (
            <FormSection title="דגלים (לקריאה בלבד)">
              <FormGrid cols={2}>
                <FormField label="חוב פתוח" value={customer.flagDebt ? "כן" : "לא"} disabled />
                <FormField
                  label="הרשאה עומדת לפוג"
                  value={customer.flagMandateExpiring ? "כן" : "לא"}
                  disabled
                />
                <FormField label="בסיכון" value={customer.flagAtRisk ? "כן" : "לא"} disabled />
                <FormField
                  label="ותק (ימים)"
                  value={customer.tenureDays != null ? String(customer.tenureDays) : "—"}
                  disabled
                />
                <FormField
                  label="יתרת חוב (אגורות)"
                  value={customer.balanceDue != null ? String(customer.balanceDue) : "—"}
                  disabled
                />
              </FormGrid>
            </FormSection>
          ) : null}
        </FormCard>
      </FormDialog>

      <FormDialog
        open={mergeOpen}
        onClose={() => {
          if (!mergeBusy) {
            setMergeOpen(false);
            setSaveError(null);
          }
        }}
        size="sm"
      >
        <FormCard
          title={`מיזוג ${labels.customer}`}
          error={saveError}
          onSave={() => {
            if (mergeTargetId) {
              setMergeConfirmOpen(true);
            }
          }}
          onCancel={() => {
            setMergeOpen(false);
            setSaveError(null);
          }}
          saveLabel="המשך"
          cancelLabel="ביטול"
          saving={false}
          saveDisabled={!mergeTargetId}
        >
          <FormSection>
            <FormGrid cols={1}>
              <FormField
                label={`${labels.customer} יעד (יישאר)`}
                type="select"
                value={mergeTargetId}
                onChange={setMergeTargetId}
                options={customerOptions.map((entry) => ({
                  value: entry.id,
                  label: entry.name,
                }))}
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>

      <ConfirmDialog
        open={archiveOpen}
        onClose={() => {
          if (!archiveBusy) {
            setArchiveOpen(false);
            setArchiveCancelReasonKey("");
          }
        }}
        title={`ביטול ${labels.customer}`}
        confirmText="ביטול"
        cancelText="חזרה"
        loading={archiveBusy}
        onConfirm={() => {
          void handleArchive();
        }}
        onCancel={() => {
          setArchiveOpen(false);
          setArchiveCancelReasonKey("");
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ margin: 0 }}>
            {`לבטל את "${customer.name}"? הסטטוס יעבור למבוטל.`}
          </p>
          <LookupSelect
            label="סיבת ביטול"
            variant="fixed"
            value={archiveCancelReasonKey}
            onChange={setArchiveCancelReasonKey}
            options={cancelReasonOptions}
            disabled={archiveBusy}
            allowEmpty
            emptyLabel="בחרו סיבה…"
            required
          />
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={Boolean(aliasToDelete)}
        onClose={() => setAliasToDelete(null)}
        title="מחיקת כינוי"
        message={
          aliasToDelete ? `למחוק את הכינוי "${aliasToDelete.alias}"?` : ""
        }
        confirmText="מחיקה"
        cancelText="ביטול"
        loading={deleteAliasBusy}
        onConfirm={() => {
          void handleDeleteAlias();
        }}
        onCancel={() => setAliasToDelete(null)}
      />

      <ConfirmDialog
        open={mergeConfirmOpen}
        onClose={() => setMergeConfirmOpen(false)}
        title="אישור מיזוג"
        message={`למזג את "${customer.name}" לתוך "${mergeTargetName}"? ${labels.customer} הנוכחי יועבר לארכיון.`}
        confirmText="מיזוג"
        cancelText="ביטול"
        loading={mergeBusy}
        onConfirm={() => {
          void handleMerge();
        }}
        onCancel={() => setMergeConfirmOpen(false)}
      />
    </>
  );
}
